import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useExperiment } from '../../context/ExperimentContext';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc, increment, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';

export const PostRequest = () => {
  const { user, profile } = useAuth();
  const { isPointsEnabled } = useExperiment();
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [urgentQuota, setUrgentQuota] = useState({ used: 0, limit: 1 });
  
  const subjects = ['Math', 'English', 'Science', 'History', 'Geography', 'Physics', 'Chemistry', 'Biology', 'Chinese', 'French', 'Economics', 'Business', 'Computer Science', 'Other'];
  const EXAM_MONTHS = [4, 5, 10, 11]; // May, Jun, Nov, Dec

  useEffect(() => {
    const checkQuota = async () => {
      if (!user) return;
      const now = new Date();
      const isExamMonth = EXAM_MONTHS.includes(now.getMonth());
      const limit = isExamMonth ? 2 : 1;
      
      try {
        const q = query(collection(db, 'requests'), where('requesterId', '==', user.uid));
        const snap = await getDocs(q);
        let used = 0;
        snap.forEach(docSnap => {
          const data = docSnap.data();
          const createdDate = data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : new Date(data.createdAt);
          if (createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear() && data.isUrgent) {
            used++;
          }
        });
        setUrgentQuota({ used, limit });
      } catch (err) { console.error('Quota check error:', err); }
    };
    checkQuota();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      let currentPoints = userData.points || 0;

      // ✅ Weekly Point Recovery (+2 every 7 days)
      if (isPointsEnabled) {
        const lastRecovery = userData.lastWeeklyRecovery?.toDate?.() || userData.lastWeeklyRecovery;
        const now = new Date();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        
        if (!lastRecovery || (now - new Date(lastRecovery)) > sevenDaysMs) {
          currentPoints += 2;
          await updateDoc(userRef, { points: currentPoints, lastWeeklyRecovery: serverTimestamp() });
        }

        if (currentPoints < 1) {
          setError('Not enough points! You need at least 1 point.');
          setLoading(false);
          return;
        }
      }

      // ✅ Urgent Quota Check
      if (isUrgent && urgentQuota.used >= urgentQuota.limit) {
        const now = new Date();
        const isExamMonth = EXAM_MONTHS.includes(now.getMonth());
        setError(`Urgent limit reached! (${urgentQuota.limit} per month ${isExamMonth ? '(Exam Period)' : ''}). Try again next month.`);
        setLoading(false);
        return;
      }

      // ✅ DE-IDENTIFIED: Only store UIDs, NO names/emails
      await addDoc(collection(db, 'requests'), {
        requesterId: user.uid,
        subject, title, description,
        pointsCost: isPointsEnabled ? 1 : 0,
        isUrgent,
        status: 'open',
        createdAt: serverTimestamp(),
        offerCount: 0
      });

      if (isPointsEnabled) await updateDoc(userRef, { points: increment(-1) });

      alert('Request posted successfully!');
      navigate('/');
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const remainingUrgents = urgentQuota.limit - urgentQuota.used;
  const now = new Date();
  const isExamMonth = EXAM_MONTHS.includes(now.getMonth());

  return (
    <div className="post-request-page">
      <Card className="post-request-card">
        <h2>📝 Post a Tutoring Request</h2>
        {isPointsEnabled && <p className="subtitle">Cost: 1 point (You have: {profile?.points || 0} points)</p>}
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Subject</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} required>
              <option value="">Select a subject</option>
              {subjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Help with..." required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe..." rows="4" required />
          </div>
          
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '1rem' }}>
            <input type="checkbox" id="urgent" checked={isUrgent} onChange={(e) => setIsUrgent(e.target.checked)} style={{ width: 'auto', transform: 'scale(1.2)', marginTop: '4px' }} disabled={remainingUrgents <= 0} />
            <div>
              <label htmlFor="urgent" style={{ fontWeight: '600', color: remainingUrgents > 0 ? '#dc2626' : '#999', cursor: remainingUrgents > 0 ? 'pointer' : 'not-allowed' }}>🚨 Mark as Urgent</label>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#666' }}>
                {remainingUrgents > 0 ? `${remainingUrgents} urgent request${remainingUrgents === 1 ? '' : 's'} remaining this month` : 'Urgent quota exhausted'}
                {isExamMonth && <span style={{ color: '#d97706', fontWeight: 600 }}> (Exam Period: +1 extra)</span>}
              </p>
            </div>
          </div>

          {isPointsEnabled && (
            <div className="cost-info" style={{ background: '#f0fdf4', color: '#166534', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' }}>
              💡 <strong>Point System:</strong> You automatically recover 2 points every 7 days.
            </div>
          )}

          <div className="btn-group">
            <Button type="button" onClick={() => navigate(-1)} className="btn-outline">Cancel</Button>
            <Button type="submit" disabled={loading || (isUrgent && remainingUrgents <= 0)} className="btn-primary">{loading ? 'Posting...' : 'Post Request'}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
